import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActiveToast, IndividualConfig, Toast, ToastContainerDirective, ToastrService } from "ngx-toastr";
import { Observable, Subject, takeUntil } from 'rxjs';
import { ActionCallback } from './external/api';
import { UserNotificationType } from './external/hub';
import { TranslationService } from './translation.service';
import { SubscribeNotificationComponent } from '../pages/user/components/notification-records/templates/subscribe-notification/subscribe-notification.component';

const toastrConfig/*: IndividualConfig*/ = {
    timeOut: 1500,
};

export enum MsgType {
    Warn,
    Err
}

export enum PinnedMsgAction {
    Paymnet,
    Refresh
}
export interface IPinnedMsgModel {
    id: number;
    text: string;
    type: MsgType;
    action?: PinnedMsgAction;
}

export interface AskEvent {
    question: string;
    title: string;
    result: ActionCallback<boolean>;
}

export enum PromptFieldType {
    Number,
    Text,
    Check,
    Pwd,
}

export interface PromptField {
    name: string;
    type: PromptFieldType;
    value: any;
}

export interface PromptEvent {
    title: string;
    fields: PromptField[];
    result: ActionCallback<PromptField[]>;
    okname: string;
}


export interface INotificationRecord<T> {
    title: string;
    body: string;
    config?: Partial<IndividualConfig<T>>;
    payload?: T;
    notificationType?: "success" | "error" | "warning" | "info" | "subscribe";
}

export interface INotificationCoutResponse {
    count: number;
    color: string;
}

export interface INotificationResponse<T> {
    actionButton: string;
    payload?: T
}

class NotificationMetadata {
    subscription: string
}

const notificationConfig: Partial<IndividualConfig<NotificationMetadata>> = {
    disableTimeOut: true,
    enableHtml: true,
    closeButton: true,
    positionClass: "inline",
    newestOnTop: true,
    tapToDismiss: false
};

@Injectable()
export class AlertsService {

    private pinsChangeEvent: Subject<void> = new Subject<void>();
    private askEvent: Subject<AskEvent> = new Subject<AskEvent>();
    private promptEvent: Subject<PromptEvent> = new Subject<PromptEvent>();
    private notificationCountEvent: Subject<INotificationCoutResponse> = new Subject<INotificationCoutResponse>();
    private notificationOpenEvent: Subject<void> = new Subject<void>();
    private beepAudio = new Audio('/assets/alerts/click3.mp3');
    private lastBeepAt = new Date().getTime();
    private toastNotificationContainer: ToastContainerDirective = null;

    public pins: Map<number, IPinnedMsgModel> = new Map<number, IPinnedMsgModel>();


    public getPinChanges = () => this.pinsChangeEvent.asObservable();
    public getAskEvent = () => this.askEvent.asObservable();
    public getPromptEvent = () => this.promptEvent.asObservable();
    public getNoificationCountEvent = (cancelToken: Subject<void>) => this.notificationCountEvent.asObservable().pipe(takeUntil(cancelToken));
    public getNoificationOpenEvent = (cancelToken: Subject<void>) => this.notificationOpenEvent.asObservable().pipe(takeUntil(cancelToken));

    notificationCounter: Map<number, ActiveToast<any>> = new Map<number, ActiveToast<any>>();

    constructor(private snackBar: MatSnackBar,
        private toastrBar: ToastrService,
        private translate: TranslationService ) {
        this.translate.langChanged = () => {
            this.pin(UserNotificationType.Language, this.translate.getNotify(UserNotificationType.Language), false, PinnedMsgAction.Refresh);
        };
    }

    openNotificationMenu = () => {
        this.notificationOpenEvent.next();
    }

    setNotifyContainer = (container: ToastContainerDirective) => this.toastNotificationContainer = container;

    notify = <T>(msg: INotificationRecord<T>, cancelToken: Subject<void>): Observable<INotificationResponse<T>> => {
        this.toastrBar.overlayContainer = this.toastNotificationContainer || this.toastrBar.overlayContainer;
        let cnf = { ...msg.config, ...notificationConfig };
        cnf.payload = msg.payload;
        let event = this.notifyToastr(msg.body, msg.title, msg.notificationType, cnf);
        this.notificationCounter.set(event.toastId, event);
        const notificationActionEvent = new Subject<INotificationResponse<T>>();

        this.notificationCountEvent.next({ color: "accent", count: this.notificationCounter.size });

        event.onAction.subscribe((item) => {
            notificationActionEvent.next(item);
        });
        event.onHidden.subscribe(() => {
            this.notificationCounter.delete(event.toastId);
            this.notificationCountEvent.next({ color: "accent", count: this.notificationCounter.size });
        });
        event.onTap.subscribe(() => {
            this.notificationCounter.delete(event.toastId);
            this.notificationCountEvent.next({ color: "accent", count: this.notificationCounter.size });
        });

        return notificationActionEvent.asObservable().pipe(takeUntil(cancelToken));

    }

    snack(msg: string, err: boolean) {
        this.snackBar.open(msg, null);
    }

    userSnack(msg: string) {
        this.snackBar.open(msg, 'OK');
    }

    notifyToastr = (msg: string,
        title: string = null,
        notifications: "success" | "error" | "warning" | "subscribe" | "info" = "success",
        cfg: Partial<IndividualConfig<any>> = null): ActiveToast<any | object> => {
        switch (notifications) {
            case "info":
                return this.notifyInfo(msg, title, cfg);
            case "error":
                return this.notifyError(msg, title, cfg);
            case "success":
                return this.notifySuccess(msg, title, cfg);
            case "warning":
                return this.notifyWarning(msg, title, cfg);
            case "subscribe": {
                cfg.toastComponent = SubscribeNotificationComponent;
                cfg.toastClass = 'ngx-toastr toast-subscribe';
                return this.notifyCustom(msg, title, cfg);
            }

        }
    }

    notifyError = (msg: string, title: string = null, cfg: any = null): ActiveToast<any | object> => this.toastrBar.error(msg, title, cfg || notificationConfig);
    notifySuccess = (msg: string, title: string = null, cfg: any = null): ActiveToast<any | object> => this.toastrBar.success(msg, title, cfg || notificationConfig);
    notifyWarning = (msg: string, title: string = null, cfg: any = null): ActiveToast<any | object> => this.toastrBar.warning(msg, title, cfg || notificationConfig);
    notifyCustom = (msg: string, title: string = null, cfg: any = null): ActiveToast<any | object> => this.toastrBar.show(msg, title, cfg || notificationConfig);
    notifyInfo = (msg: string, title: string = null, cfg: any = null): ActiveToast<any | object> => this.toastrBar.info(msg, title, cfg || notificationConfig);

    notifySaved = (title: string = null, cfg: any = null): ActiveToast<any | object> => this.notifySuccess('Saved', title, cfg || notificationConfig);
    notifyDeleted = (title: string = null, cfg: any = null): ActiveToast<any | object> => this.notifySuccess('Deleted', title, cfg || notificationConfig);
    notifOK = (title: string = null, cfg: any = null): ActiveToast<any | object> => this.notifySuccess('Ok', title, cfg || notificationConfig);

    toastr = (msg: string, err: boolean = false) => {
        if (err)
            return this.toastrBar.error(msg, null, toastrConfig);
        else
            return this.toastrBar.success(msg, '', toastrConfig);
    }


    saved = () => this.toastr(this.translate.translate('UserInfo.Saved'));
    deleted = () => this.toastr(this.translate.translate('UserInfo.Deleted'));
    ok = (msg: string = null) => this.toastr(msg ?? 'Ok');
    error = (msg: string) => this.toastr(msg, true);

    promptText(name: string, value: string = null, title: string = null, okname: string = null): Promise<string> {
        return this.prompt([{
            name: name,
            type: PromptFieldType.Text,
            value: value
        }], title, okname).then(x => {
            if (x != null)
                return x[0].value as string;
            return null as string;
        });
    }
    promptNumber(name: string, value: number = null, title: string = null): Promise<number> {
        return this.prompt([{
            name: name,
            type: PromptFieldType.Number,
            value: value
        }], title).then(x => {
            if (x != null)
                return x[0].value as number;
            return null as number;
        });
    }
    prompt(fields: PromptField[], title: string = null, okname: string = null): Promise<PromptField[]> {
        return new Promise(res => {
            this.promptEvent.next({
                result: res,
                fields: fields,
                title: title,
                okname: okname
            });
        });
    }
    ask(question: string, title: string = 'UserInfo.Confirm'): Promise<boolean> {
        return new Promise(res => {
            this.askEvent.next({
                result: res,
                question: question,
                title: title
            });
        });
    }
    askSave = (name: string): Promise<boolean> => this.ask(`${this.translate.translate('User.Save')} ${name}?`);

    askDelete = (name: string): Promise<boolean> => this.ask(`${this.translate.translate('User.Delete')} ${name}?`);

    pin(msgId: UserNotificationType, msg: string, err: boolean = false, action: PinnedMsgAction = null) {
        this.pins.set(msgId, { id: msgId, text: msg, type: err ? MsgType.Err : MsgType.Warn, action: action });
        this.pinsChangeEvent.next();
    }

    public unpin = (msgId: number) => {
        this.pins.delete(msgId);
        this.pinsChangeEvent.next();
    }

    public beep() {
        const at = new Date().getTime();
        if (at - this.lastBeepAt > 3000) {
            this.lastBeepAt = at;
            this.beepAudio.play();
        }
    }


}
